import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { donorsService } from '../services/api';
import DonorForm from './DonorForm';
import './Donors.css';
import { useAuth } from '../contexts/AuthContext';
import DonorImportModal from './DonorImportModal';

const Donors = () => {
  const navigate = useNavigate();
  const [donors, setDonors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentYear] = useState(new Date().getFullYear());
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  const { isAdmin } = useAuth();
  const [showImportModal, setShowImportModal] = useState(false);

  // Fetch donors
  const fetchDonors = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await donorsService.list({
        page,
        limit,
        year: currentYear,
        search: searchTerm || undefined
      });

      setDonors(response.data.data);
      setTotal(response.data.pagination.total);
      setTotalPages(response.data.pagination.pages);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch donors');
      console.error('Error fetching donors:', err);
    } finally {
      setLoading(false);
    }
  }, [page, limit, currentYear, searchTerm]);

  useEffect(() => {
    fetchDonors();
  }, [fetchDonors]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1); // Reset to first page on search
  };

  const handleDonorClick = (donor_id) => {
    navigate(`/donors/${donor_id}`);
  };

  const handleDeleteDonor = async (donor_id, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this donor?')) {
      try {
        await donorsService.delete(donor_id);
        setDonors(donors.filter(d => d.id !== donor_id));
        alert('Donor deleted successfully');
      } catch (err) {
        alert('Failed to delete donor: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  const handleFormSubmit = (formData) => {
    setDonors([formData, ...donors]);
    setShowForm(false);
    fetchDonors(); // Refresh list to get proper pagination
  };

  const handlePreviousPage = () => {
    if (page > 1) setPage(page - 1);
  };

  const handleNextPage = () => {
    if (page < totalPages) setPage(page + 1);
  };

  const handleImportContacts = async () => {
    setShowImportModal(false);
    await fetchDonors();
  };

  const handleExportEmails = async () => {
    try {
      setLoading(true);
      const allDonors = [];
      let currentPage = 1;
      let hasMore = true;

      // Fetch all donors with pagination
      while (hasMore) {
        const response = await donorsService.list({
          page: currentPage,
          limit: 100,
          year: currentYear
        });

        allDonors.push(...response.data.data);
        hasMore = currentPage < response.data.pagination.pages;
        currentPage++;
      }

      if (allDonors.length === 0) {
        alert('No donors to export');
        setLoading(false);
        return;
      }

      // Create CSV header
      const headers = ['Donor Name', 'Donor Email'];
      
      // Create CSV rows
      const rows = allDonors.map(donor => [
        `${donor.first_name} ${donor.last_name}`,
        donor.email || ''
      ]);

      // Combine headers and rows
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"` ).join(','))
      ].join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `donor-emails-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      alert(`Exported ${allDonors.length} donors`);
    } catch (err) {
      alert('Failed to export emails: ' + (err.response?.data?.error || err.message));
      console.error('Export error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="donors-container">
      <div className="page-header">
        <h1>
          Donors
          <button 
            className="btn btn-primary"
            onClick={() => setShowForm(true)}
          >
            + New
          </button>
        </h1>
      </div>

      {showForm && (
        <DonorForm 
          onSubmit={handleFormSubmit}
          onCancel={() => setShowForm(false)}
        />
      )}

      {showImportModal && (
        <DonorImportModal 
          onClose={() => setShowImportModal(false)}
          onImport={handleImportContacts}
        />
      )}

      <div className="search-bar">
        <input
          type="text"
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={handleSearch}
          className="search-input"
        />
        <span className="search-info">
          Showing donations from {currentYear} • Total donors: {total}
        </span>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {loading ? (
        <div className="loading">Loading donors...</div>
      ) : donors.length === 0 ? (
        <div className="empty-state">
          <p>No donors found. {searchTerm ? 'Try a different search.' : 'Add your first donor!'}</p>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="donors-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th className="amount-column">{currentYear} Total</th>
                  {isAdmin && (
                    <th className="actions-header">
                      <button 
                        className="btn btn-sm btn-secondary"
                        onClick={() => setShowImportModal(true)}
                        disabled={loading}
                        title="Import contacts from CSV"
                      >
                        Import Contacts
                      </button>
                      <button 
                        className="btn btn-sm btn-secondary"
                        onClick={handleExportEmails}
                        disabled={loading}
                        title="Export all donors"
                      >
                        Export Emails
                      </button>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {donors.map(donor => (
                  <tr 
                    key={donor.id}
                    className="table-row"
                    onClick={() => handleDonorClick(donor.id)}
                  >
                    <td className="name-cell">
                      {donor.first_name} {donor.last_name}
                    </td>
                    <td>{donor.email || '-'}</td>
                    <td className="amount-column">
                      ${Number(donor.current_year_total || '0.00').toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </td>
                    {isAdmin && (
                      <td className="actions-cell">
                        <button 
                          className="btn btn-sm btn-danger"
                          onClick={(e) => handleDeleteDonor(donor.id, e)}
                          title="Delete"
                        >
                          Delete
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button 
                className="btn btn-outline"
                onClick={handlePreviousPage}
                disabled={page === 1}
              >
                ← Previous
              </button>
              <span className="page-info">
                Page {page} of {totalPages}
              </span>
              <button 
                className="btn btn-outline"
                onClick={handleNextPage}
                disabled={page === totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Donors;
